import { formatDate, formatDateTimeWithSeconds } from '../../utils/DateTimeUtils';
import {
    Button,
    Datagrid,
    EditButton,
    ExportButton,
    FunctionField,
    List,
    TextField,
    TopToolbar,
    useListContext,
    useRecordContext,
    useResourceContext,
} from "react-admin";
import { ChefForPartyBookingTypeEnum } from "../../enums/ChefForPartyBookingTypeEnum";
import CustomReferenceField from "../Common/CustomReferenceField";
import ChefForPartStatusEnum from "../../enums/ChefForPartStatusEnum";
import {PaymentStatusEnum} from "../../enums/PaymentStatusEnum";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import {TimeEnum} from "../../enums/TimeEnum";
import CallButton from "../Common/CallButton/CallButton";
import { UserType } from "../../enums/common";
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import {useNavigate} from "react-router-dom";
import CityResponse from "../../interfaces/City/CityResponse";
import { useEffect, useState } from "react";
import CityService from "../../services/CityService";
import { CookDetailTypeEnumChefForParty } from "../../enums/CookDetailTypeEnumChefForParty";
import AdvancedCookFilterDialog from "../Cooks/AdvancedCookFilterDialog";
import FilterListIcon from '@mui/icons-material/FilterList';
import { Box, Chip } from "@mui/material";
import UpdateLeadTagDialog from "../Common/UpdateLeadTagDailog";
import RosterService from "../../services/RosterService";
import { getChipColors, TrackingStatusEnum } from "../VisitTracker/VisitTrackerList";
import CommonFilterCard, { CommonFilterField } from "../Common/CommonFilterCard";
import StyledDatagridWrapper, { commonDatagridSx } from "../Common/StyledDatagridWrapper";

const cityService=new CityService();
const rosterService=new RosterService();

let RenderCTAButtons = () => {
    let [updatePartyTag, setUpadatePartyTag] = useState<boolean>(false);
    // Rendered as the Datagrid's bulkActionButtons, i.e. inside the List's
    // context, so read the real selection from there instead of a prop threaded
    // from a second, competing list controller.
    const { selectedIds } = useListContext();

    return (
      <Box ml={4} display={'flex'} gap={4}>
      {
            updatePartyTag &&
            <UpdateLeadTagDialog
              open = {updatePartyTag}
              setOpen={setUpadatePartyTag}
              data ={selectedIds}
              isMultiple = {true}
            />
      }
        <Button onClick={()=> setUpadatePartyTag(true)} variant="outlined" label="Update Leads"/>
      </Box>
    )
  }

// Selection-dependent Datagrid styling lives here so it reads selectedIds from
// INSIDE the List's context. Doing this at the ChefForPartList top level required
// a second useListController() for the same resource, which competed with the
// List's own controller over the URL/store filter params and wiped persisted
// filters on every render/navigation.
const SelectionAwareDatagrid = ({ children }: { children: any }) => {
    const { selectedIds } = useListContext();
    const resource = useResourceContext();
    return (
        <Datagrid
            // Both modules share the party show page; open it under the current
            // resource so the sidebar keeps highlighting the right module.
            rowClick={(id) => `/${resource}/${id}/show`}
            bulkActionButtons={<RenderCTAButtons />}
            sx={{
                ...commonDatagridSx,
                // The global .toolbar-custom-css rule gives every toolbar a
                // min-height, which keeps the collapsed bulk-actions bar
                // visible and overlapping the header — hide it fully until
                // something is selected.
                ...(selectedIds?.length !== 0
                    ? { mt: "3rem", mb: "3rem" }
                    : { "& .RaBulkActionsToolbar-toolbar": { display: "none" } }),
            }}
        >
            {children}
        </Datagrid>
    );
};

const EditButtonView = () => {
    const record = useRecordContext();
    if (![ChefForPartStatusEnum.FULFILLED, ChefForPartStatusEnum.CANCELLED, ChefForPartStatusEnum.RESCHEDULED].includes(record.status)) {
        return (
            <EditButton />
        )
    }
    return null
}

export enum PartyTag{
    HOT = "HOT",
    COLD = "COLD",
    JUNK = "JUNK",
    LOST = "LOST",
    CONVERTED = "CONVERTED",
    FRESH = "FRESH"
}

const ListActions = (props: any) => {
    const { setFilters } = useListContext();
    const resetFilter = () => {
      setFilters({}, [], false);
    };
    const navigate = useNavigate();
    return (
      <TopToolbar>
        <Button
          variant="text"
          size={"small"}
          onClick={resetFilter}
          startIcon={<FilterListOffIcon />}
          label="Clear filters"
        />
        <Button
            variant="text"
            size={"small"}
            onClick={() => {
            props?.setOpenAdvancedFilter(true);
            }}
            startIcon={<FilterListIcon />}
            color="primary"
            label="Advance Filter"
        />
        <Button
          variant="text"
          size={"small"}
          onClick={() => {
            const bookingType = props?.bookingType;
            const query = bookingType ? `?type=${bookingType}` : '';
            navigate(`/ChefForParty/create${query}`);
          }}
          startIcon={<ControlPointIcon />}
          label="Create"
        />
        <ExportButton/>
      </TopToolbar>
    );
};

const ChefForPartList = (props: any) => {
    const [cities, setCities] = useState<CityResponse[]>([]);
    const [openAdvancedFilter, setOpenAdvancedFilter] = useState(false);
    const [additionlFilters,setAdditionalFilters] = useState<any>([]);
    const[owners,setOwners]=useState<any>([])

    // This list backs two modules: the "ChefForParty" resource and the
    // "BookByDish" resource. Derive the booking type from the active resource so
    // each module shows only its own bookings and — because react-admin keys the
    // filter store by resource name — persists its filters independently.
    const resource = useResourceContext(props);
    const bookingType = resource === "BookByDish"
        ? ChefForPartyBookingTypeEnum.BOOK_BY_DISH
        : ChefForPartyBookingTypeEnum.CHEF_FOR_PARTY;

    let navigate = useNavigate();


    const filterFields: CommonFilterField[] = [
        {
            type: "multiselect",
            label: "Status",
            source: "status",
            options: Object.values(ChefForPartStatusEnum).map((key) => ({
                value: key,
                label: key,
            })),
        },
        {
            type: "select",
            label: "Token Amount Payment Status",
            source: "tokenAmountPaymentStatus",
            minWidth: 220,
            options: Object.values(PaymentStatusEnum).map((key) => ({
                value: key,
                label: key,
            })),
        },
        {
            type: "select",
            label: "Remaining Amount Payment Status",
            source: "remainingAmountPaymentStatus",
            minWidth: 250,
            options: Object.values(PaymentStatusEnum).map((key) => ({
                value: key,
                label: key,
            })),
        },
        {
            type: "select",
            label: "Second Payment Recieved",
            source: "anyPaymentRecieved",
            minWidth: 210,
            options: [
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" },
            ],
        },
        { type: "date", label: "Date", source: "date" },
        { type: "text", label: "Customer Phone", source: "customerPhone" },
        { type: "text", label: "Party Id", source: "id", numeric: true },
        { type: "text", label: "Cook Id", source: "cookId", numeric: true },
        { type: "text", label: "Cook Name", source: "cookName" },
        {
            type: "select",
            label: "City",
            source: "cityId",
            options: cities.map((eachCity: any) => ({
                value: eachCity?.id,
                label: eachCity?.name,
            })),
        },
        {
            type: "select",
            label: "Time",
            source: "time",
            options: Object.values(TimeEnum).map((value) => ({
                value,
                label: value,
            })),
        },
        { type: "date", label: "Created At Start Date", source: "startDate" },
        { type: "date", label: "Created At End Date", source: "endDate" },
        { type: "date", label: "Start Date", source: "startPartyDate" },
        { type: "date", label: "End Date", source: "endPartyDate" },
        {
            type: "select",
            label: "Owner Name",
            source: "ownerEmail",
            options: owners
                ? [
                      ...owners.map((owner: any) => ({
                          value: owner?.adminDetails?.email,
                          label: owner?.adminDetails?.name,
                      })),
                      { value: "none", label: "NONE" },
                  ]
                : [],
        },
        {
            type: "multiselect",
            label: "Tracking Status",
            source: "trackingStatus",
            options: Object.values(TrackingStatusEnum).map((value) => ({
                value,
                label: value,
            })),
        },
    ];

    useEffect(() => {
        cityService.fetchAllCities(100, 0, {isCurrentlyServing:"YES"}, {}).then((res) => {
          setCities(res?.data || []);
        });
      }, []);

      useEffect(() => {
        rosterService
          .getAllEmployeesForRoster("CHEF_FOR_PARTY")
          .then((res) => {
            setOwners(res);
          });
    }, [])  

    let getBartenderAndWaiterCount = (record: any) => {
        let bartender = 0;
        let waiter = 0;
        record?.orders?.map((eachOrder: any) => {
            if(eachOrder?.orderItems?.length !== 0){
                eachOrder?.orderItems.map((eachOrderItem: any) => {
                    if(eachOrderItem?.serviceAddons?.id === 1){
                        bartender += Number(eachOrderItem?.quantity);
                    }
                    if(eachOrderItem?.serviceAddons?.id === 2){
                        waiter += Number(eachOrderItem?.quantity);
                    }
                    return eachOrderItem;
                })
            }
            return eachOrder;
        })

        return {bartender, waiter};
    }  

    const CookField = ({
        label,
        type,
        showIndex = false,
      }: {
        label: string;
        type: CookDetailTypeEnumChefForParty;
        showIndex?: boolean;
      }) => (
        <FunctionField
          label={label}
          render={(record: any) => {
            const cooks = record?.cooks?.filter(
              (cook: any) => cook?.type === type
            ) || [];
      
            if (cooks.length === 0) {
              return <div>-</div>;
            }
      
            return cooks.map((cook: any, i: number) => (
              <div key={cook?.details?.id}>
                {showIndex ? `${i + 1}. ` : ""}
                {cook?.details?.cookName}
              </div>
            ));
          }}
        />
    );

    const getBackground = (type: String) => {
        switch (type) {
            case PartyTag.HOT:
                return 'orange';
            case PartyTag.COLD:
                return 'skyblue';
            case PartyTag.CONVERTED:
                return 'green';
            case PartyTag.JUNK:
                return '#A0522D';
            case PartyTag.FRESH:
                return 'purple'     // valid color
            default:
                return 'red';
        }
    };
    return (
        <div >
            <List
            {...props}
             filter={{ bookingType }}
             storeKey={resource}
             sort={{ field: "id", order: "DESC" }}
             actions={<ListActions setOpenAdvancedFilter={setOpenAdvancedFilter} bookingType={bookingType} />}
             className="toolbar-custom-css"
             sx={{
               "& .RaList-content": {
                 boxShadow: "none",
                 backgroundColor: "transparent",
               },
             }}
             >
                <>
                <CommonFilterCard fields={filterFields} />

                <br />

                <StyledDatagridWrapper
                    title={resource === "BookByDish" ? "Book By Dish" : "Chef For Party"}
                >
                <SelectionAwareDatagrid>
                    <FunctionField
                        render={(record: any) => {
                            const color = getBackground(record?.partyTag);
                            return (
                                <Box
                                    sx={{
                                        padding:'0.2rem',
                                        backgroundColor: color,
                                        borderRadius: '20px',
                                        textAlign: 'center',
                                        color: 'white'
                                    }}
                                >
                                    {record?.partyTag}
                                </Box>
                            );
                        }}
                    />
                    <TextField source='id' />
                    <FunctionField label="Date" sortBy="date" render={(record: any) => (
                        <span style={{ whiteSpace: 'nowrap', display: 'inline-block', minWidth: '90px' }}>
                          {record?.date ? formatDate(record.date) : '-'}
                        </span>
                    )} />
                    <FunctionField label="Time" sortBy="time" render={(record: any) => (
                        <span style={{ whiteSpace: 'nowrap', display: 'inline-block', minWidth: '70px' }}>
                          {record?.time || '-'}
                        </span>
                    )} />
                    <FunctionField label="House" render={(_record: any) => (
                        <span onClick={(e) => e.stopPropagation()}>
                            <CustomReferenceField label="" listName={"Houses"} source={"houseId"} />
                        </span>
                    )} />
                    <TextField source='cityName' />
                    <TextField source='customerName' />
                    {/* <TextField source='customerPhone' /> */}
                    <FunctionField label="Customer Phone" render={(record: any) =>
                        <CallButton toPhoneNumber={record.customerPhone} userType={UserType.CUSTOMER} referenceId={record?.id} referenceModelName="CHEF_FOR_PARTY"/>
                    } />
                    <FunctionField
                        label="Net Amount"
                        render={(record: any) =>
                            (Number(record?.totalAmount) - Number(record?.totalDiscount)).toFixed(2)
                        }
                    />
                    <FunctionField
                        label="Waiter"
                        render={(record: any) =>
                            getBartenderAndWaiterCount(record).waiter
                        }
                    />
                    <FunctionField
                        label="Bartender"
                        render={(record: any) =>
                            getBartenderAndWaiterCount(record).bartender
                        }
                    />
                    <CookField label="Cook" type={CookDetailTypeEnumChefForParty.MAIN_CHEF} />
                    <FunctionField
                    label="Chef Phone Number"
                    render={(record: any) => {
                        const cooks = record?.cooks?.filter(
                            (cook: any) => cook?.type === CookDetailTypeEnumChefForParty.MAIN_CHEF
                          ) || [];
                    
                          if (cooks.length === 0) {
                            return <div>-</div>;
                          }
                    
                          return cooks.map((cook: any) => (
                            <div key={cook?.details?.id}>
                              <CallButton
                                toPhoneNumber={cook?.details?.cookPhone}
                                userType={UserType.COOK}
                                referenceId={record?.id}
                                referenceModelName="CHEF_FOR_PARTY"
                            />
                            </div>
                          ));
                    }}
                    />
                    <CookField
                        label="Assistant Chef(s)"
                        type={CookDetailTypeEnumChefForParty.ASSISTANT_CHEF}
                        showIndex
                    />
                    <CookField
                        label="Bartender(s)"
                        type={CookDetailTypeEnumChefForParty.BARTENDER}
                        showIndex
                    />
                    <CookField
                        label="Waiter(s)"
                        type={CookDetailTypeEnumChefForParty.WAITER}
                        showIndex
                    />
                    <TextField source='noOfAdults' />
                    <TextField source='noOfChildren' />
                    <TextField source='noOfDishes' />
                    <TextField source='totalAmount' label={'Total Amount '} />
                    <TextField source='amount_to_be_collected' />
                    <TextField source='tokenAmountPaymentStatus' />
                    <TextField source='remainingAmountPaymentStatus' />
                    <TextField source='partialPaymentStatus' />
                    <FunctionField
                        label="Allocator Comments"
                        render={(record: any) =>
                            <div>{record?.allocatorComments?.length ? record?.allocatorComments?.map((comment : any ) => comment?.comment ).join(', '):<></>}</div>
                        }
                    />
                    <TextField source='status' />
                    <FunctionField label="Created At" render={(record: any) => (
                        <span style={{ whiteSpace: 'nowrap' }}>
                          {record?.createdAt ? formatDateTimeWithSeconds(record.createdAt) : '-'}
                        </span>
                    )} />
                    <TextField source='ownerName' />
                    <FunctionField
                    label="Tracking Status"
                    render={(record: any) => (
                        <Chip
                        label={record?.trackingStatus}
                        style={{
                            backgroundColor:getChipColors(record?.trackingStatus).backgroundColor,
                            color: getChipColors(record?.trackingStatus).color,
                            fontWeight: 500,
                            fontSize: "12px",
                        }}
                        />
                    )}
                    />
                    <TextField source='trackingNotes' />
                    
                    <FunctionField
                        label="Tracker"
                        render={(record: any) =>
                            [ChefForPartStatusEnum.CHEF_CONFIRMED,
                            ChefForPartStatusEnum.CHEF_ALLOTTED,
                            ChefForPartStatusEnum.FULFILLED,
                            ChefForPartStatusEnum.ONGOING,
                            ].includes(record?.status) ? (
                            <Button
                                variant="contained"
                                sx={{
                                backgroundColor: "#2E7D32",
                                "&:hover": {
                                    backgroundColor: "#1B5E20",
                                },
                                }}
                                label="View Tracker"
                                onClick={(e: any) => {
                                e.stopPropagation();
                                const cookId = record?.cooks[0]?.details?.cookId
                                navigate(`/VisitTracker/CHEF_FOR_PARTY/${record?.id}/${cookId}`, {
                                    state: {
                                    visitData: record,
                                    cookId: record.cooks
                                        ?.filter((cook: any) => cook.type === "MAIN_CHEF")[0]
                                        ?.details?.cookId,
                                    },
                                });
                                }}
                            />
                            ) : null
                        }
                        />
                    <FunctionField label="" render={()=><EditButtonView/>}/>
                </SelectionAwareDatagrid>
                </StyledDatagridWrapper>
                <AdvancedCookFilterDialog setOpenAdvancedFilter={setOpenAdvancedFilter} openAdvancedFilter={openAdvancedFilter} filter={additionlFilters} setFilter={setAdditionalFilters}/>
                </>
            </List>
        </div>
    )
}

export default ChefForPartList;
